import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { 
  TreeDeciduous, 
  MapPin, 
  Award,
  Trees,
  Heart,
  Loader2
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

export default function MyForestPage() {
  const { t } = useLanguage();
  const { user, isAuthenticated, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [trees, setTrees] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchMyForest();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  const fetchMyForest = async () => {
    try {
      const res = await fetch(`${API}/my-forest`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setTrees(data.trees || []);
        setCertificates(data.certificates || []);
      }
    } catch (e) {
      console.error('Failed to fetch my forest:', e);
    } finally {
      setLoading(false);
    }
  };

  const getSpeciesName = (code) => {
    const species = {
      lenga: 'Lenga Beech',
      nire: 'Ñire Beech',
      coihue: 'Coihue',
      cipres: 'Patagonian Cypress',
      araucaria: 'Monkey Puzzle'
    };
    return species[code] || code;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="my-forest-page">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background" data-testid="my-forest-page">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-lg mx-auto text-center">
            <Trees className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
            <h1 className="text-3xl font-bold mb-4">{t('myForest.title')}</h1>
            <p className="text-muted-foreground mb-8">
              Sign in to view your adopted trees
            </p>
            <Button onClick={login} size="lg" className="rounded-full" data-testid="login-btn">
              {t('nav.login')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="my-forest-page">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div 
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="my-forest-title">
              {t('myForest.title')}
            </h1>
            <p className="text-lg text-muted-foreground">{t('myForest.subtitle')}</p>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="p-6 text-center">
                <TreeDeciduous className="w-8 h-8 mx-auto mb-2 text-primary" />
                <div className="text-3xl font-bold">{trees.length}</div>
                <p className="text-sm text-muted-foreground">{t('myForest.totalTrees')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Award className="w-8 h-8 mx-auto mb-2 text-accent" />
                <div className="text-3xl font-bold">{certificates.length}</div>
                <p className="text-sm text-muted-foreground">{t('myForest.certificate')}s</p>
              </CardContent>
            </Card>
            <Card className="col-span-2 sm:col-span-1">
              <CardContent className="p-6 text-center">
                <Heart className="w-8 h-8 mx-auto mb-2 text-red-500" />
                <div className="text-3xl font-bold">
                  {Math.round(trees.length * 0.02 * 10) / 10}
                </div>
                <p className="text-sm text-muted-foreground">Tons CO₂/year</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Trees Grid */}
          {trees.length === 0 ? (
            <motion.div 
              className="text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Trees className="w-24 h-24 mx-auto text-muted-foreground/30 mb-6" />
              <h2 className="text-2xl font-semibold mb-4">{t('myForest.noTrees')}</h2>
              <Button asChild size="lg" className="rounded-full" data-testid="adopt-now-btn">
                <Link to="/adopt">
                  <Heart className="w-4 h-4 mr-2" />
                  {t('myForest.adoptNow')}
                </Link>
              </Button>
            </motion.div>
          ) : (
            <motion.div 
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.1 } }
              }}
            >
              {trees.map((tree, index) => (
                <motion.div
                  key={tree.tree_id}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 }
                  }}
                >
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`tree-card-${index}`}>
                    <div className="h-40 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center relative">
                      <TreeDeciduous className="w-20 h-20 text-primary/40 group-hover:scale-110 transition-transform" />
                      <Badge className="absolute top-3 right-3 bg-accent">
                        {t('tree.adopted')}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-lg mb-2">{getSpeciesName(tree.species)}</h3>
                      <p className="text-xs font-mono text-muted-foreground mb-3">{tree.tree_id}</p>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                        <MapPin className="w-4 h-4" />
                        <span className="mono text-xs">
                          {tree.lat.toFixed(4)}, {tree.lng.toFixed(4)}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <Button asChild variant="outline" size="sm" className="flex-1">
                          <Link to={`/tree/${tree.tree_id}`}>
                            {t('common.view')}
                          </Link>
                        </Button>
                        <Button asChild variant="secondary" size="sm" className="flex-1">
                          <Link to={`/map?tree=${tree.tree_id}`}>
                            <MapPin className="w-4 h-4 mr-1" />
                            Map
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
